import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, LayoutList, Network } from 'lucide-react';
import ModalTabs from '../ui/ModalTabs';
import Tooltip from '../ui/Tooltip';
import { DbBrandIcon } from '../../extensions/dbBrandIcons';
import { DB_ENGINE_LABELS } from '../../extensions/dbEngines';
import { openDbManagerTab } from '../../store/tabsStore';
import { patchDbOverviewTab } from '../../store/dbOverviewTabs';
import { overviewContext, invalidateOverview, loadFacts } from './dbOverviewCatalog';
import { factsFor } from './dbOverviewFacts';
import DbOverviewObjects from './DbOverviewObjects';
import './DbOverview.css';

// The diagram (and d3-force with it) only loads when someone opens it.
const DbSchemaDiagram = React.lazy(() => import('./DbSchemaDiagram'));

/**
 * A database's overview tab — DBeaver's database editor, DataGrip's schema
 * view: what is in the database (Tables, Views, Indexes, … with sizes and row
 * estimates) and, on the Diagram half, how the tables relate.
 *
 * Read-only by construction: everything here is a catalog SELECT. Opening a
 * table goes to its ordinary data tab.
 */
export default function DbOverviewTab({ tab }) {
  const { t } = useTranslation();
  const ctx = useMemo(() => overviewContext(tab), [tab.engine, tab.extensionId, tab.connId, tab.dbName]); // eslint-disable-line react-hooks/exhaustive-deps
  const mode = tab.overviewMode === 'diagram' ? 'diagram' : 'objects';
  // Bumped by Refresh: every section, the counts and the diagram re-read.
  const [nonce, setNonce] = useState(0);
  const [facts, setFacts] = useState(null);
  const [factsError, setFactsError] = useState(null);

  useEffect(() => {
    let alive = true;
    setFactsError(null);
    loadFacts(ctx)
      .then((f) => alive && setFacts(f))
      .catch((e) => alive && setFactsError(e?.message || String(e)));
    return () => {
      alive = false;
    };
  }, [ctx, nonce]);

  const refresh = useCallback(() => {
    invalidateOverview(ctx);
    setNonce((n) => n + 1);
  }, [ctx]);

  const openTable = useCallback(
    (rec, { newTab = false, isView = false } = {}) => {
      if (!rec?.name) return;
      const dbName = tab.engine === 'postgres' ? rec.schema || tab.dbName || 'public' : tab.dbName || 'main';
      openDbManagerTab({
        extensionId: tab.extensionId,
        connId: tab.connId,
        engine: tab.engine,
        dbName,
        tableName: rec.name,
        label: isView ? `${rec.name} (view)` : rec.name,
        newTab,
      });
    },
    [tab.extensionId, tab.connId, tab.engine, tab.dbName]
  );

  const setMode = (next) => patchDbOverviewTab(tab.id, { overviewMode: next });
  const showInDiagram = (key) => patchDbOverviewTab(tab.id, { overviewMode: 'diagram', diagramFocus: key });

  const engineLabel = DB_ENGINE_LABELS[tab.engine] || tab.engine;
  const factList = factsFor(tab.engine, facts, { scope: ctx.scope });
  const subtitle =
    tab.engine === 'postgres' && ctx.scope
      ? t('dbOverview.schemaIn', { db: facts?.name || tab.connLabel || '' })
      : tab.connLabel && tab.connLabel !== tab.label
        ? tab.connLabel
        : null;

  return (
    <div className="dbm-tab dbo-tab" data-testid="db-overview">
      <div className="dbo-topbar">
        <ModalTabs
          tabs={[
            { id: 'objects', icon: LayoutList, label: t('dbOverview.mode.overview') },
            { id: 'diagram', icon: Network, label: t('dbOverview.mode.diagram') },
          ]}
          activeTab={mode}
          onTabChange={setMode}
        />
        <div className="dbo-topbar-spacer" />
        <Tooltip content={t('dbOverview.refreshHint')} side="bottom">
          <button type="button" className="dbm-icon-btn" onClick={refresh} aria-label={t('dbOverview.refresh')}>
            <RefreshCw size={14} />
          </button>
        </Tooltip>
      </div>

      <header className="dbo-header">
        <div className="dbo-title-row">
          <span className="dbo-engine-icon" aria-hidden>
            <DbBrandIcon name={tab.engine} size={18} />
          </span>
          <h2 className="dbo-title">{tab.label}</h2>
          <span className="dbo-engine-tag">{engineLabel}</span>
          {subtitle && <span className="dbo-subtitle">{subtitle}</span>}
        </div>
        {factsError ? (
          <div className="dbo-facts-error">{factsError}</div>
        ) : (
          <dl className="dbo-facts" aria-label={t('dbOverview.properties')}>
            {factList.map((f) => (
              <div key={f.key} className="dbo-fact">
                <dt>{t(`dbOverview.fact.${f.key}`)}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </header>

      {mode === 'objects' ? (
        <DbOverviewObjects
          tab={tab}
          ctx={ctx}
          nonce={nonce}
          onOpenTable={openTable}
          onShowInDiagram={showInDiagram}
        />
      ) : (
        <Suspense
          fallback={
            <div className="dbm-empty dbm-running" role="status" aria-label={t('dbOverview.loading')}>
              <span className="dbm-spinner" aria-hidden />
            </div>
          }
        >
          <DbSchemaDiagram tab={tab} ctx={ctx} nonce={nonce} onOpenTable={openTable} />
        </Suspense>
      )}
    </div>
  );
}
